import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { CAPS } from '@/lib/kbCaps';
import { extractKbFieldErrors, type FieldErrorMap } from '@/lib/kbFieldErrors';
import { FieldError } from '@/components/onboarding/enrichment/FieldError';
import { CharCounter } from '@/components/shared/CharCounter';

interface PoliciesEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: string | null;
}

export function PoliciesEditor({ open, onOpenChange, initialValue }: PoliciesEditorProps) {
  const { t } = useTranslation();
  const { updateKbProfile } = useOnboardingState();
  const [value, setValue] = useState(initialValue ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setFieldErrors(null);
    try {
      await updateKbProfile({ policies: value || null });
      onOpenChange(false);
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('bot.enrich.policies.title')}</DialogTitle>
          <DialogDescription>{t('bot.enrich.policies.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label htmlFor="policies-text">{t('bot.enrich.policies.field_label')}</Label>
          <Textarea
            id="policies-text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('bot.enrich.policies.placeholder')}
            maxLength={CAPS.policies}
            rows={5}
          />
          <CharCounter value={value} max={CAPS.policies} />
          <FieldError errors={fieldErrors} path="policies" />
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="min-h-11"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-11">
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
